import type { Guide } from '@fruitful/types';
import {
    BodyMediumEmphasized,
    BodySmallRegular,
    ExternalLinkIcon,
    MonoLabelRegular,
    PillButton,
    XStack,
    YStack,
} from 'smoothie';

import { getRequiredGuideMediaUrls } from '../GuideMedia';
import { GuideMediaStatusBadge } from './GuideMediaStatusBadge';

export const RequiredGuideMediaStatusBadges = ({ guide }: { guide: Guide }) => {
    // Here we should gather the url for every media file required by our product
    // and render a <GuideMediaStatusBadge> for it so we can quickly see whether
    // any guide has an issue with something required.
    const requiredGuideMediaUrls = getRequiredGuideMediaUrls(guide.signUpId);
    return (
        <>
            {requiredGuideMediaUrls?.map(({ label, url, mediaType }) => (
                <GuideMediaStatusBadge
                    key={url}
                    url={url}
                    label={label}
                    mediaType={mediaType}
                />
            ))}
        </>
    );
};

export const RequiredGuideMediaInstructions = ({
    guideSignupId,
}: {
    guideSignupId: string | null | undefined;
}) => {
    guideSignupId =
        guideSignupId != null && guideSignupId.trim() !== ''
            ? guideSignupId
            : '{{signUpId}}';
    const requiredGuideMediaUrls = getRequiredGuideMediaUrls(guideSignupId);

    return (
        <YStack gap={12}>
            <XStack alignItems="center" gap={12}>
                <BodyMediumEmphasized>Required Media</BodyMediumEmphasized>
                <a
                    href="https://us-east-2.console.aws.amazon.com/s3/buckets/fruitful-assets?region=us-east-2&prefix=product/guide-assets/&showversions=false"
                    target="_blank"
                    rel="noreferrer"
                    style={{ textDecoration: 'none' }}
                >
                    <PillButton
                        text="Open S3"
                        variant="default"
                        icon={ExternalLinkIcon}
                        iconPosition="right"
                        onPress={() => {}}
                    />
                </a>
            </XStack>
            <BodySmallRegular>
                The following media is required by the member app for all
                guides. Ensure the correct files are uploaded to S3 at the
                specified locations.
            </BodySmallRegular>
            <YStack gap={12} paddingHorizontal={8}>
                {requiredGuideMediaUrls?.map(({ label, url, mediaType }) => {
                    const i = url.indexOf(guideSignupId);
                    const [base, rest] =
                        i >= 0 ? [url.slice(0, i), url.slice(i)] : [url, ''];
                    return (
                        <XStack key={url} gap={8} alignItems="center">
                            <XStack width={90}>
                                <GuideMediaStatusBadge
                                    url={url}
                                    label={label}
                                    mediaType={mediaType}
                                />
                            </XStack>
                            <MonoLabelRegular flex={1} color="$gray900">
                                {base}
                                <MonoLabelRegular
                                    width="80%"
                                    display="inline"
                                    color="$black"
                                >
                                    {rest}
                                </MonoLabelRegular>
                            </MonoLabelRegular>
                        </XStack>
                    );
                })}
            </YStack>
        </YStack>
    );
};
